'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { getSpeedsizeImageUrl } from './custom-image/CustomImage';
import styles from './ProductCarousel.module.css';
import {
  useEffect,
  useCallback,
  useContext,
  useState,
  useRef,
  useMemo,
} from 'react';
import gsap from 'gsap';
import { CarouselButton } from '../shared';
import { PHONE_MAX_WIDTH_IN_PX } from '../theme';
import { GlobalUIContext } from 'contexts/GlobalUIContext';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { useTranslations } from 'next-intl';

export interface ProductCarouselItem {
  // Image props
  src?: string;
  srcResponsive?: string;
  thumb?: string;
  alt?: string;
  /** When true, `src` / `srcResponsive` are local `/public` paths and
   *  bypass the speedsize CDN loader (rendered via plain next/image). */
  local?: boolean;

  // Video props (mutually exclusive with image)
  videoSrc?: string;
  videoSrcResponsive?: string;
  poster?: string;
  posterResponsive?: string;

  // Common props
  objectFit?: 'cover' | 'contain';
  objectPositionBottom?: boolean;
  background?: string;

  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  textPosition?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'middle-left'
    | 'middle-center'
    | 'middle-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';

  // Per-slide autoplay duration (overrides carousel default)
  autoSlideDuration?: number; // Duration in seconds
}

interface ProductCarouselProps {
  className?: string;
  items: ProductCarouselItem[];
  sticky?: boolean;
  navigationType?: 'buttons' | 'cursorTracked' | 'none';
  buttonVariant?: 'dark' | 'light' | 'glass';
  autoSlideDuration?: number; // Duration in seconds
  pauseOnHover?: boolean; // Pause auto-slide on hover (default: false)
}

// ============================================================================
// HELPERS & CONSTANTS
// ============================================================================
const hasPointerSupport = () =>
  typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches;

const INDICATOR_PROXIMITY_THRESHOLD = 12;
const PROGRESS_START_DELAY = 0.24;
const DIRECTION_CHANGE_DELAY = 100;

// Match next/image's /w_828 for non-priority slides; priority slide-0 stays full.
const NON_PRIORITY_IMAGE_WIDTH = 828;
/* Desktop <img> cap for non-priority slides — mirrors main's next/image
   `sizes="(min-width:1024px) 70vw"` fix (was serving 828 upscaled). */
const NON_PRIORITY_IMAGE_WIDTH_DESKTOP = 2000;

const getMediaSource = (
  item: ProductCarouselItem,
  isVideo: boolean,
  isMobile: boolean,
) => {
  const mobileKey = isVideo ? 'videoSrcResponsive' : 'srcResponsive';
  const desktopKey = isVideo ? 'videoSrc' : 'src';
  const result = isMobile
    ? item[mobileKey] || item[desktopKey]
    : item[desktopKey] || item[mobileKey];

  // Debug logging for videos
  // if (isVideo) {
  //   console.log('getMediaSource:', {
  //     isMobile,
  //     mobileKey,
  //     desktopKey,
  //     videoSrc: item.videoSrc,
  //     videoSrcResponsive: item.videoSrcResponsive,
  //     result,
  //   });
  // }

  return result;
};

const getPosterSource = (item: ProductCarouselItem, isMobile: boolean) => {
  return isMobile
    ? item.posterResponsive || item.poster
    : item.poster || item.posterResponsive;
};

const TEXT_POSITION_CLASS: Record<string, string> = {
  'top-left': styles.topLeft,
  'top-center': styles.topCenter,
  'top-right': styles.topRight,
  'middle-left': styles.middleLeft,
  'middle-center': styles.middleCenter,
  'middle-right': styles.middleRight,
  'bottom-left': styles.bottomLeft,
  'bottom-center': styles.bottomCenter,
  'bottom-right': styles.bottomRight,
};

export const ProductCarousel = ({
  className,
  items,
  sticky,
  navigationType = 'buttons',
  buttonVariant = 'light',
  autoSlideDuration,
  pauseOnHover = false,
}: ProductCarouselProps) => {
    // ========================================================================
    // STATE & REFS
    // ========================================================================
    const t = useTranslations('common');
    const { headerVisible } = useContext(GlobalUIContext);
    const { width } = useWindowSize();
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Refs for video and DOM elements
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const carouselContainerRef = useRef<HTMLDivElement | null>(null);
    const indicatorRef = useRef<HTMLDivElement | null>(null);
    const ctaContainerRef = useRef<HTMLDivElement | null>(null);
    const itemsRef = useRef(items);

    // Update items ref when items prop changes
    useEffect(() => {
      itemsRef.current = items;
    }, [items]);

    // Cursor tracking state
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
    const [isHovering, setIsHovering] = useState(false);
    const [isNearIndicator, setIsNearIndicator] = useState(false);
    const [cursorDirection, setCursorDirection] = useState<'prev' | 'next'>(
      'next',
    );
    const [isChangingDirection, setIsChangingDirection] = useState(false);

    // ========================================================================
    // AUTOPLAY: Setup custom autoplay with per-slide duration support
    // ========================================================================
    const progressTween = useRef<gsap.core.Tween | null>(null);
    const progressRefs = useRef<Array<HTMLDivElement | null>>([]);
    const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isAutoplayPausedRef = useRef(false);

    const plugins = useMemo(() => [], []);

    // ========================================================================
    // EMBLA CAROUSEL: Initialize carousel
    // ========================================================================
    const [emblaRef, emblaApi] = useEmblaCarousel(
      {
        align: 'start',
        loop: true,
        slidesToScroll: 1,
        containScroll: 'trimSnaps',
      },
      plugins,
    );

    // ========================================================================
    // PROGRESS BAR: Animation helpers
    // ========================================================================
    const killProgressTween = useCallback(() => {
      if (progressTween.current) {
        progressTween.current.kill();
        progressTween.current = null;
      }
    }, []);

    const resetAllBars = useCallback(() => {
      progressRefs.current.forEach((el) => {
        if (el) {
          gsap.set(el, { clearProps: 'all', width: '0%' });
        }
      });
    }, []);

    // ========================================================================
    // SELECTION: Handle slide changes
    // ========================================================================
    const onSelect = useCallback(() => {
      if (!emblaApi) return;
      const index = emblaApi.selectedScrollSnap();
      setSelectedIndex(index);
    }, [emblaApi]);

    // ========================================================================
    // CUSTOM AUTOPLAY: Start/stop/reset timer
    // ========================================================================
    const clearAutoplayTimer = useCallback(() => {
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
    }, []);

    const startCustomAutoplay = useCallback(() => {
      if (!emblaApi || !autoSlideDuration || isAutoplayPausedRef.current)
        return;

      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }

      // Get per-slide duration or fallback to default (use ref to avoid dependency)
      const currentItem = itemsRef.current[selectedIndex];
      const duration =
        (currentItem?.autoSlideDuration ?? autoSlideDuration ?? 0) * 1000;

      if (duration > 0) {
        // Start progress animation
        killProgressTween();
        resetAllBars();
        const activeEl = progressRefs.current[selectedIndex];
        if (activeEl) {
          gsap.set(activeEl, { width: '0%' });
          progressTween.current = gsap.to(activeEl, {
            width: '100%',
            duration: Math.max(0, duration) / 1000,
            ease: 'none',
            delay: PROGRESS_START_DELAY,
          });
        }

        // Set timer to advance to next slide
        autoplayTimerRef.current = setTimeout(() => {
          emblaApi.scrollNext();
        }, duration + PROGRESS_START_DELAY * 1000);
      }
    }, [
      emblaApi,
      autoSlideDuration,
      selectedIndex,
      killProgressTween,
      resetAllBars,
    ]);

    const stopCustomAutoplay = useCallback(() => {
      clearAutoplayTimer();
      killProgressTween();
    }, [clearAutoplayTimer, killProgressTween]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const resetAutoplay = useCallback(() => {
      stopCustomAutoplay();
      startCustomAutoplay();
    }, [stopCustomAutoplay, startCustomAutoplay]);

    // ========================================================================
    // NAVIGATION: Scroll with autoplay reset
    // ========================================================================
    const scrollPrev = useCallback(() => {
      if (!emblaApi) return;
      stopCustomAutoplay();
      emblaApi.scrollPrev();
    }, [emblaApi, stopCustomAutoplay]);

    const scrollNext = useCallback(() => {
      if (!emblaApi) return;
      stopCustomAutoplay();
      emblaApi.scrollNext();
    }, [emblaApi, stopCustomAutoplay]);

    // ========================================================================
    // AUTOPLAY & SELECTION: Wire up Embla events
    // ========================================================================
    useEffect(() => {
      if (!emblaApi) return;

      onSelect();
      emblaApi.on('select', onSelect);

      return () => {
        emblaApi.off('select', onSelect);
      };
    }, [emblaApi, onSelect]);

    // Start custom autoplay when slide changes
    useEffect(() => {
      if (!emblaApi || !autoSlideDuration) return;
      startCustomAutoplay();

      return () => {
        stopCustomAutoplay();
      };
    }, [
      emblaApi,
      selectedIndex,
      autoSlideDuration,
      startCustomAutoplay,
      stopCustomAutoplay,
    ]);

    // ========================================================================
    // INTERSECTION OBSERVER: Pause/resume carousel when out of view
    // ========================================================================
    useEffect(() => {
      if (!carouselContainerRef.current || !emblaApi || !autoSlideDuration)
        return;

      const container = carouselContainerRef.current;
      let timeoutId: NodeJS.Timeout;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Clear any pending state changes
            clearTimeout(timeoutId);

            // Debounce to prevent rapid toggling during fast scrolls (especially on iOS Safari)
            timeoutId = setTimeout(() => {
              if (entry.isIntersecting) {
                // Resume autoplay when carousel is in view
                isAutoplayPausedRef.current = false;
                startCustomAutoplay();
              } else {
                // Pause autoplay when carousel is out of view
                isAutoplayPausedRef.current = true;
                stopCustomAutoplay();
              }
            }, 150);
          });
        },
        {
          threshold: 0.3,
          rootMargin: '0px 0px -10% 0px',
        },
      );

      observer.observe(container);

      return () => {
        clearTimeout(timeoutId);
        observer.disconnect();
      };
    }, [emblaApi, autoSlideDuration, startCustomAutoplay, stopCustomAutoplay]);

    // ========================================================================
    // PAUSE ON HOVER: Handle pauseOnHover functionality
    // ========================================================================
    useEffect(() => {
      if (!pauseOnHover || !autoSlideDuration || !carouselContainerRef.current)
        return;

      const container = carouselContainerRef.current;

      const handleMouseEnter = () => {
        isAutoplayPausedRef.current = true;
        stopCustomAutoplay();
      };

      const handleMouseLeave = () => {
        isAutoplayPausedRef.current = false;
        startCustomAutoplay();
      };

      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
      };
    }, [
      pauseOnHover,
      autoSlideDuration,
      startCustomAutoplay,
      stopCustomAutoplay,
    ]);

    // ========================================================================
    // VIDEO MANAGEMENT: Play/pause videos based on active slide
    // ========================================================================
    useEffect(() => {
      videoRefs.current.forEach((video, index) => {
        if (!video) return;

        if (index === selectedIndex) {
          // Play video when slide becomes active
          // Use requestAnimationFrame to ensure video element is ready
          requestAnimationFrame(() => {
            if (video) {
              video.currentTime = 0;
              video.load(); // Reload video source
              video.play().catch((err) => {
                console.warn('Video autoplay failed:', err);
              });
            }
          });
        } else {
          // Pause and reset video when slide becomes inactive
          video.pause();
          video.currentTime = 0;
        }
      });
    }, [selectedIndex]);

    // Ensure first video plays on mount (especially important for mobile)
    useEffect(() => {
      if (!emblaApi) return;

      // Small delay to ensure video element and refs are ready
      const timer = setTimeout(() => {
        const firstVideo = videoRefs.current[0];
        if (firstVideo && selectedIndex === 0) {
          firstVideo.currentTime = 0;
          firstVideo.load();
          firstVideo.play().catch((err) => {
            console.warn('Initial video autoplay failed:', err);
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }, [emblaApi]);

    // ========================================================================
    // CURSOR TRACKING: Setup mouse event listeners for cursor-tracked mode
    // ========================================================================
    useEffect(() => {
      if (
        navigationType !== 'cursorTracked' ||
        !hasPointerSupport() ||
        !carouselContainerRef.current
      )
        return;

      const container = carouselContainerRef.current;

      const handleMouseMove = (e: MouseEvent) => {
        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setCursorPosition({ x, y });

        let isNear = false;

        // Check if cursor is near indicator dots
        if (indicatorRef.current) {
          const indicatorRect = indicatorRef.current.getBoundingClientRect();
          const indicatorX = indicatorRect.left - rect.left;
          const indicatorY = indicatorRect.top - rect.top;

          // Check if cursor is within threshold distance of indicator
          isNear =
            x >= indicatorX - INDICATOR_PROXIMITY_THRESHOLD &&
            x <=
              indicatorX +
                indicatorRect.width +
                INDICATOR_PROXIMITY_THRESHOLD &&
            y >= indicatorY - INDICATOR_PROXIMITY_THRESHOLD &&
            y <=
              indicatorY + indicatorRect.height + INDICATOR_PROXIMITY_THRESHOLD;
        }

        // Check if cursor is near CTA container
        if (!isNear && ctaContainerRef.current) {
          const ctaRect = ctaContainerRef.current.getBoundingClientRect();
          const ctaX = ctaRect.left - rect.left;
          const ctaY = ctaRect.top - rect.top;

          // Check if cursor is within threshold distance of CTA container
          isNear =
            x >= ctaX - INDICATOR_PROXIMITY_THRESHOLD &&
            x <= ctaX + ctaRect.width + INDICATOR_PROXIMITY_THRESHOLD &&
            y >= ctaY - INDICATOR_PROXIMITY_THRESHOLD &&
            y <= ctaY + ctaRect.height + INDICATOR_PROXIMITY_THRESHOLD;
        }

        setIsNearIndicator(isNear);
      };

      const handleMouseEnter = () => {
        setIsHovering(true);
      };

      const handleMouseLeave = () => {
        setIsHovering(false);
      };

      const handleClick = (e: MouseEvent) => {
        // Don't handle clicks on CTA buttons or indicator dots
        const target = e.target as HTMLElement;
        if (
          ctaContainerRef.current?.contains(target) ||
          indicatorRef.current?.contains(target)
        ) {
          return;
        }

        const x = e.clientX - container.getBoundingClientRect().left;
        if(x < container.offsetWidth / 2){
          scrollPrev();
        } else {
          scrollNext();
        }
      };

      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);
      container.addEventListener('click', handleClick);

      return () => {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
        container.removeEventListener('click', handleClick);
      };
    }, [navigationType, scrollPrev, scrollNext]);

    // ========================================================================
    // CURSOR DIRECTION: Handle direction changes with animation
    // ========================================================================
    const currentDirection = useMemo(() => {
      if (!carouselContainerRef.current) return 'next';
      return cursorPosition.x < carouselContainerRef.current.offsetWidth / 2
        ? 'prev'
        : 'next';
    }, [cursorPosition.x]);

    useEffect(() => {
      if (currentDirection !== cursorDirection && !isNearIndicator) {
        setIsChangingDirection(true);
        const timer = setTimeout(() => {
          setCursorDirection(currentDirection);
          setIsChangingDirection(false);
        }, DIRECTION_CHANGE_DELAY);
        return () => clearTimeout(timer);
      }
    }, [currentDirection, cursorDirection, isNearIndicator]);

    // Determine effective navigation type (fallback for non-pointer devices)
    const effectiveNavigationType = useMemo(
      () =>
        navigationType === 'cursorTracked' && !hasPointerSupport()
          ? 'none'
          : navigationType,
      [navigationType],
    );

    // isMobile now only drives video slides (images use <picture>); desktop until width resolves.
    const isMobile = width > 0 ? width <= PHONE_MAX_WIDTH_IN_PX : false;

    // Slides render in SSR (root class reserves the box height); embla attaches on mount.
    const carouselContent = (
      <div
        className={[
          styles.root,
          className,
          effectiveNavigationType === 'cursorTracked'
            ? styles.cursorTracked
            : '',
          isNearIndicator ? styles.nearIndicator : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-autoslide={autoSlideDuration ? 'true' : 'false'}
        ref={carouselContainerRef}
        suppressHydrationWarning
      >
        <div className={`${styles.embla} embla`} ref={emblaRef}>
          <div className={`${styles.emblaContainer} embla__container`}>
            {items.map((item, index) => {
              const isVideo = Boolean(item.videoSrc || item.videoSrcResponsive);
              const mediaSrc = getMediaSource(item, isVideo, isMobile);
              const posterSrc = getPosterSource(item, isMobile);
              const slideImageWidth =
                index === 0 ? undefined : NON_PRIORITY_IMAGE_WIDTH;
              const mediaClasses = [
                isVideo ? `${styles.mainVideo} main-video` : `${styles.mainImage} main-image`,
                item.objectPositionBottom ? `${styles.objectBottom} object-bottom` : '',
                item.objectFit === 'cover'
                  ? `${styles.objectFitCover} object-fit-cover`
                  : item.objectFit === 'contain'
                    ? `${styles.objectFitContain} object-fit-contain`
                    : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <div key={index} className={`${styles.emblaSlide} embla__slide`}>
                  <div
                    className={`${styles.imageContainer} image-container`}
                    style={{ background: item.background }}
                    suppressHydrationWarning
                  >
                    {isVideo ? (
                      <video
                        key={mediaSrc}
                        ref={(el) => {
                          videoRefs.current[index] = el;
                        }}
                        className={mediaClasses}
                        poster={posterSrc}
                        playsInline
                        muted
                        preload="metadata"
                        suppressHydrationWarning
                      >
                        <source src={mediaSrc} type="video/mp4" />
                      </video>
                    ) : item.src || item.srcResponsive ? (
                      <picture>
                        {item.srcResponsive && (
                          <source
                            media={`(max-width: ${PHONE_MAX_WIDTH_IN_PX}px)`}
                            srcSet={getSpeedsizeImageUrl(
                              item.srcResponsive,
                              slideImageWidth,
                            )}
                          />
                        )}
                        <img
                          src={getSpeedsizeImageUrl(
                            item.src || item.srcResponsive || '',
                            slideImageWidth && NON_PRIORITY_IMAGE_WIDTH_DESKTOP,
                          )}
                          alt={
                            item.alt ||
                            t('productCarousel.image.alt.ultrahumanImage')
                          }
                          className={mediaClasses}
                          width={400}
                          height={400}
                          loading={index === 0 ? 'eager' : 'lazy'}
                          fetchPriority={index === 0 ? 'high' : undefined}
                          suppressHydrationWarning
                        />
                      </picture>
                    ) : null}

                    {(item.title || item.description) && (
                      <div
                        className={[
                          `${styles.slideTextWrapper} slide-text-wrapper`,
                          TEXT_POSITION_CLASS[item.textPosition || 'top-center'],
                          selectedIndex === index ? `${styles.active} active` : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {item.title && (
                          <h3 className={styles.title}>{item.title}</h3>
                        )}
                        {item.description && (
                          <p className={styles.description}>
                            {item.description}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Navigation - Standard Buttons */}
        {effectiveNavigationType === 'buttons' && (
          <div className={`${styles.navigation} navigation`}>
            <CarouselButton
              direction="prev"
              variant={buttonVariant}
              rounded="full"
              fill={true}
              onClick={scrollPrev}
              aria-label={t('productCarousel.carouselButton.ariaLabel.previousImage')}
              style={{ position: 'relative' }}
            />
            <CarouselButton
              direction="next"
              variant={buttonVariant}
              rounded="full"
              fill={true}
              onClick={scrollNext}
              aria-label={t('productCarousel.carouselButton.ariaLabel.nextImage')}
              style={{ position: 'relative' }}
            />
          </div>
        )}

        {/* Navigation - Cursor Tracked */}
        {effectiveNavigationType === 'cursorTracked' && isHovering && (
          <div
            className={[
              `${styles.cursorTracker} cursor-tracker`,
              isNearIndicator || isChangingDirection ? styles.hidden : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              left: `${cursorPosition.x}px`,
              top: `${cursorPosition.y}px`,
            }}
          >
            <CarouselButton
              direction={cursorDirection}
              variant={buttonVariant}
              rounded="full"
              fill={true}
              aria-label={
                cursorDirection === 'prev'
                  ? t('productCarousel.carouselButton.ariaLabel.previous')
                  : t('productCarousel.carouselButton.ariaLabel.next')
              }
              style={{ position: 'relative', pointerEvents: 'none' }}
            />
          </div>
        )}

        {/* CTA Navigation Container - Only for cursor-tracked mode */}
        {effectiveNavigationType === 'cursorTracked' && items.length > 1 && (
          <div className={`${styles.ctaNavWrapper} cta-nav-wrapper`} ref={ctaContainerRef}>
            <CarouselButton
              direction="prev"
              variant={buttonVariant}
              rounded="full"
              fill={true}
              onClick={scrollPrev}
              aria-label={t('productCarousel.carouselButton.ariaLabel.previousSlide')}
              style={{ position: 'relative' }}
            />
            <CarouselButton
              direction="next"
              variant={buttonVariant}
              rounded="full"
              fill={true}
              onClick={scrollNext}
              aria-label={t('productCarousel.carouselButton.ariaLabel.nextSlide')}
              style={{ position: 'relative' }}
            />
          </div>
        )}

        {/* Indicator Dots */}
        {items.length > 1 && (
          <div className={`${styles.indicatorWrapper} indicator-wrapper`} ref={indicatorRef}>
            {items.map((_, i) => (
              <button
                className={[`${styles.dot} dot`, i === selectedIndex ? `${styles.isActive} is-active` : '']
                  .filter(Boolean)
                  .join(' ')}
                key={i}
                onClick={() => {
                  if (!emblaApi) return;
                  stopCustomAutoplay();
                  emblaApi.scrollTo(i);
                }}
                aria-label={t('productCarousel.carouselButton.ariaLabel.goToSlide', { index: i + 1 })}
              >
                <div
                  className={`${styles.progress} progress`}
                  ref={(el) => {
                    progressRefs.current[i] = el;
                  }}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    );

    return sticky ? (
      <div
        className={styles.stickyWrapper}
        data-header-visible={headerVisible ? 'true' : 'false'}
      >
        {carouselContent}
      </div>
    ) : (
      carouselContent
    );
};
